/* eslint @typescript-eslint/strict-boolean-expressions: off */

import { ApolloError, gql, QueryResult } from "@apollo/client";
import * as Sentry from "@sentry/react";
import type { ActiveContext } from "shared/ContextSwitcher/store";
import { useSession } from "src/components/session/session";
import {
  OperationCosts,
  useGetTeamOperationCostsQuery,
  useGetUserOperationCostsQuery,
} from "src/gql/codegen/graphql";
import React, { ReactNode } from "react";
import { AnchorButtonTextOnly } from "src/components/ui-components/ButtonTextOnly/AnchorButtonTextOnly";
import { LinkProps } from "next/link";
import { getQueryParamToSetActiveContext } from "shared/ContextSwitcher/store";
import { useMyTeamsIfLoggedIn } from "shared/teams/useMyTeams";
import { isPaidTier } from "src/utils/subscriptions/isPaidTier";
import { ButtonTextOnly } from "src/components/ui-components/ButtonTextOnly/ButtonTextOnly";
import { setIsMenuOpen } from "shared/ContextSwitcher/menuStore";

gql`
  query GetTeamOperationCosts($teamId: Int!) {
    team_operation_costs(team_id: $teamId) {
      key
      interactive_executions {
        dataset_id
        public_performance_tier_key
        credits
      }
    }
  }
`;

gql`
  query GetUserOperationCosts {
    user_operation_costs {
      key
      interactive_executions {
        dataset_id
        public_performance_tier_key
        credits
      }
    }
  }
`;

const PerformanceTierDisplayNames = {
  free: {
    displayName: "Free",
    description: (
      <>Run simple queries that take less than 2 minutes, at no cost.</>
    ),
  },
  medium: {
    displayName: "Medium",
    description: <>Run faster queries more reliably.</>,
  },
  large: {
    displayName: "Large",
    description: <>Speed up your more complex workloads.</>,
    renderUpsellMessage: (
      href: LinkProps["href"],
      userBelongsToPaidTeam?: boolean
    ) =>
      userBelongsToPaidTeam ? (
        <>
          Large cluster size is available on paid team plans.
          <br />
          <ButtonTextOnly
            size="S"
            theme="secondary"
            insideText
            onClick={() => setIsMenuOpen(true)}
          >
            Switch
          </ButtonTextOnly>{" "}
          your active account to your paid team to speed up your more complex
          workloads.
        </>
      ) : (
        <>
          Large cluster size is available on higher plan tiers.{" "}
          <AnchorButtonTextOnly
            theme="secondary"
            size="M"
            href={href}
            target="_blank"
          >
            Upgrade your plan
          </AnchorButtonTextOnly>{" "}
          to speed up your more complex workloads.
        </>
      ),
  },
};

type DatasetID = number;

export type SupportedPublicPerformanceTierKey =
  keyof typeof PerformanceTierDisplayNames;

export function isSupportedPublicPerformanceTierKey(
  string: string
): string is SupportedPublicPerformanceTierKey {
  return string in PerformanceTierDisplayNames;
}

export type ExecutionTiersData = {
  publicPerformanceTierKey: SupportedPublicPerformanceTierKey;
  displayName: string;
  description: ReactNode;
  credits: number;
} & DisabledExecutionTierData;

type DisabledExecutionTierData =
  | {
      disabled: true;
      upsellMessage: string | React.ReactElement;
    }
  | {};

type PerformanceTiers = Record<DatasetID, ExecutionTiersData[]>;

export function useGetPerformanceTiers(
  activeContext: ActiveContext | undefined
): Pick<QueryResult<PerformanceTiers>, "loading" | "data" | "error"> {
  const session = useSession();
  const teamId = activeContext?.id ?? 0;
  const userPaidTeams = useMyTeamsIfLoggedIn(session)?.filter((team) =>
    isPaidTier(team.service_tier.id)
  );

  const userBelongsToPaidTeam = userPaidTeams
    ? userPaidTeams.length > 0
    : false;

  const teamResult = useGetTeamOperationCostsQuery({
    skip:
      !session ||
      !session.user ||
      !activeContext ||
      activeContext.type !== "team",
    variables: { teamId },
    context: { session },
    fetchPolicy: "cache-first",
  });
  const userResult = useGetUserOperationCostsQuery({
    skip:
      !session ||
      !session.user ||
      !activeContext ||
      activeContext.type !== "user",
    context: { session },
    fetchPolicy: "cache-first",
  });

  const isTeam = activeContext && activeContext.type === "team";
  const resultData: OperationCosts | undefined = isTeam
    ? teamResult.data?.team_operation_costs
    : userResult.data?.user_operation_costs;
  const loading: boolean = isTeam ? teamResult.loading : userResult.loading;
  const error: ApolloError | undefined = isTeam
    ? teamResult.error
    : userResult.error;

  return {
    error,
    loading,
    data:
      resultData &&
      transformOperationCosts(activeContext, resultData, userBelongsToPaidTeam),
  };
}

// transformOperationCosts transforms the operation costs response from a dict of dicts to
// a dict of arrays. It also merges in display information.
function transformOperationCosts(
  activeContext: ActiveContext | undefined,
  serverData: OperationCosts,
  userBelongsToPaidTeam?: boolean
): PerformanceTiers {
  const executionTiers = serverData.interactive_executions;

  const result: PerformanceTiers = {};
  for (const executionTier of executionTiers) {
    if (!result[executionTier.dataset_id]) {
      result[executionTier.dataset_id] = [] as ExecutionTiersData[];
    }
    const publicPerformanceTierKey =
      executionTier.public_performance_tier_key as SupportedPublicPerformanceTierKey;
    if (PerformanceTierDisplayNames[publicPerformanceTierKey]) {
      result[executionTier.dataset_id]!.push({
        publicPerformanceTierKey,
        displayName:
          PerformanceTierDisplayNames[publicPerformanceTierKey].displayName,
        description:
          PerformanceTierDisplayNames[publicPerformanceTierKey].description,
        credits: executionTier.credits,
      });
    } else {
      Sentry.captureException(
        `${publicPerformanceTierKey} is not a supported performance tier`
      );
    }
  }

  // If the Large cluster is missing in the list of tiers,
  // add it as an upsell option
  const largeClusterCredits = 20;
  for (const executionTier of executionTiers) {
    if (
      !result[executionTier.dataset_id].find(
        (tier) => tier.publicPerformanceTierKey === "large"
      )
    ) {
      result[executionTier.dataset_id]!.push({
        publicPerformanceTierKey: "large",
        displayName: PerformanceTierDisplayNames["large"].displayName,
        description: PerformanceTierDisplayNames["large"].description,
        credits: largeClusterCredits,
        disabled: true,
        upsellMessage: PerformanceTierDisplayNames["large"].renderUpsellMessage(
          {
            pathname: "/pricing",
            query: getQueryParamToSetActiveContext(activeContext),
          },
          userBelongsToPaidTeam
        ),
      });
    }
  }

  Object.keys(result).forEach((key) => {
    result[Number(key)].sort((a, b) => a.credits - b.credits);
  });
  return result;
}
